import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Activity, ArrowRight, BookOpen, CheckCircle2, Clock, Loader2, Target } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { EmptyState, ScoreBar, StatCard } from "@/components/Primitives";

const ACTIVITY_LABEL = {
  lesson_completed: "Completed lesson",
  lesson_reopened: "Reopened lesson",
  exercise_submitted: "Submitted exercise",
  troubleshooting_submitted: "Completed lab",
  milestone_submitted: "Capstone milestone",
  interview_completed: "Mock interview",
  plan_activated: "Plan upgraded",
};

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/dashboard").then(({ data }) => setData(data)).catch((e) => setError(errMsg(e, "Unable to load dashboard")));
  }, []);

  if (error)
    return <p data-testid="dashboard-error" className="text-sm text-destructive">{error}</p>;
  if (!data)
    return (
      <div data-testid="dashboard-loading" className="grid place-items-center py-24">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );

  const m = data.metrics;

  return (
    <div className="space-y-10 fade-up" data-testid="dashboard-page">
      <div className="flex flex-wrap items-end justify-between gap-6">
        <div>
          <p className="overline text-primary">dashboard</p>
          <h1 className="mt-3 text-3xl sm:text-4xl font-display font-black tracking-tighter">
            {data.user.name?.split(" ")[0] || "Engineer"}, here is your track.
          </h1>
        </div>
        <Link to={data.nextAction.href} data-testid="next-action-btn">
          <Button className="rounded-none gap-2">{data.nextAction.cta} <ArrowRight className="h-4 w-4" /></Button>
        </Link>
      </div>

      <div className="border border-border p-6" data-testid="next-action-card">
        <p className="overline text-muted-foreground">recommended next action</p>
        <p className="mt-3 font-display text-xl font-bold tracking-tight">{data.nextAction.label}</p>
      </div>

      <div>
        <p className="overline text-muted-foreground">readiness composition</p>
        <div className="mt-4 border-t border-l border-border grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-6" data-testid="readiness-components">
          {data.readinessComponents.map((c) => (
            <div key={c.key} data-testid={`readiness-component-${c.key}`} className="border-r border-b border-border p-4">
              <div className="flex items-baseline justify-between">
                <p className="text-xs font-medium">{c.label}</p>
                <p className="font-mono text-xs text-muted-foreground">{c.weight}%</p>
              </div>
              <p className="mt-2 font-display text-xl font-bold tracking-tighter">{c.score}</p>
              <div className="mt-2"><ScoreBar value={c.score} /></div>
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard testId="stat-readiness" label="Job readiness" value={`${data.readinessScore}%`} sub={data.readinessComponents.map((c) => `${c.label.toLowerCase()} ${c.weight}%`).join(" · ")} icon={Target} />
        <StatCard testId="stat-assessment" label="Assessment score" value={m.assessmentScore || "—"} sub={data.hasAssessment ? "latest attempt" : "not taken yet"} icon={Activity} />
        <StatCard testId="stat-lessons" label="Lessons complete" value={`${m.lessonsCompleted}/${m.lessonsPlanned}`} sub={`${m.lessonProgressPercent}% of your plan`} icon={BookOpen} />
        <StatCard testId="stat-timeline" label="Estimated timeline" value={m.estimatedWeeks ? `${m.estimatedWeeks} wks` : "—"} sub={data.hasRoadmap ? "at your weekly hours" : "generate a roadmap"} icon={Clock} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2">
          <h2 className="font-display text-xl font-bold tracking-tight">Readiness by category</h2>
          {data.categoryReadiness.length ? (
            <div className="mt-5 border-t border-l border-border grid grid-cols-1 sm:grid-cols-2" data-testid="category-readiness">
              {data.categoryReadiness.map((c) => (
                <div key={c.category} data-testid={`readiness-${c.category}`} className="border-r border-b border-border p-4">
                  <div className="flex items-baseline justify-between">
                    <p className="text-sm font-medium">{c.categoryName}</p>
                    <p className="font-mono text-xs">{c.score}</p>
                  </div>
                  <div className="mt-2.5"><ScoreBar value={c.score} /></div>
                </div>
              ))}
            </div>
          ) : (
            <div className="mt-5">
              <EmptyState
                testId="readiness-empty"
                title="No assessment data yet"
                body="Take the skill assessment to unlock per-category readiness and a personalized roadmap."
                action={<Link to="/assessment"><Button className="rounded-none">Start assessment</Button></Link>}
              />
            </div>
          )}
        </div>

        <div>
          <h2 className="font-display text-xl font-bold tracking-tight">Recent activity</h2>
          <div className="mt-5 border border-border divide-y divide-border" data-testid="recent-activity">
            {data.recentActivity.length ? (
              data.recentActivity.map((a, i) => (
                <div key={i} className="p-4 flex gap-3 items-start">
                  <CheckCircle2 className="h-4 w-4 text-[hsl(var(--success))] mt-0.5" />
                  <div>
                    <p className="text-sm">{ACTIVITY_LABEL[a.type] || a.type}</p>
                    <p className="text-xs text-muted-foreground">{a.label}</p>
                  </div>
                </div>
              ))
            ) : (
              <p className="p-5 text-sm text-muted-foreground">Nothing yet — completing a lesson will show up here.</p>
            )}
          </div>

          {(data.strengths.length > 0 || data.gaps.length > 0) && (
            <div className="mt-6 border border-border p-5">
              <p className="overline text-muted-foreground">signal</p>
              <ul className="mt-3 space-y-1 font-mono text-xs">
                {data.strengths.map((s) => <li key={s} className="text-[hsl(var(--success))]">+ {s}</li>)}
                {data.gaps.map((s) => <li key={s} className="text-destructive">- {s}</li>)}
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
